import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import { LuPlus, LuInfo, LuArrowRight, LuCopy, LuTrash2, LuCheck, LuPencil, LuEye, LuRoute } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import HistoricoDaTela from '../components/historico/HistoricoDaTela';
import Dialog from '../components/Dialog';
import NovoTrilhoModal from '../components/NovoTrilhoModal';
import { ModeloTarefaService, type ConjuntoTrilho } from '../services/ModeloTarefaService';
import { ParametrosEmpresaService, type ParametrosEmpresa } from '../services/ParametrosEmpresaService';
import { TRILHOS_PRONTOS } from '../data/trilhosProntos';
import { layoutPadrao } from '../components/TrilhoCanvas';
import '../components/ListKit.css';
import './TarefasPadraoPage.css';

/**
 * Configurações › Vendas › Tarefas padrão — a galeria
 * (docs/propostas/tarefas-padrao-quadro.html §8).
 *
 * Os trilhos da empresa em cima, os prontos da Leme embaixo. Um ativo por vez —
 * é o que o motor usa. Abrir um leva ao quadro; "Usar" copia um pronto para a
 * empresa sem desligar o que está rodando (era o que a modal antiga não dava).
 */
const dataCurta = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';

const TarefasPadraoPage = () => {
    const navegar = useNavigate();
    const [conjuntos, setConjuntos] = useState<ConjuntoTrilho[]>([]);
    const [parametros, setParametros] = useState<ParametrosEmpresa | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [ocupado, setOcupado] = useState(false);
    const [criando, setCriando] = useState(false);
    /* Com um pronto aqui, a modal abre direto no passo 2 (o "Usar" do cartão). */
    const [inicial, setInicial] = useState<string | null>(null);
    const [aExcluir, setAExcluir] = useState<ConjuntoTrilho | null>(null);
    const [aAtivar, setAAtivar] = useState<ConjuntoTrilho | null>(null);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const [cs, p] = await Promise.all([
                ModeloTarefaService.listarConjuntos(),
                ParametrosEmpresaService.ler().catch(() => null),
            ]);
            setConjuntos(cs); setParametros(p);
        } catch (e: any) {
            aviso.erro(textoDoErro(e, 'Não foi possível carregar os trilhos.'));
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    const abrirNovo = (chave: string | null = null) => { setInicial(chave); setCriando(true); };

    /* Nome e duplicidade já vêm conferidos pela modal. */
    const criar = async (nome: string, chave: string | null) => {
        const pronto = TRILHOS_PRONTOS.find(t => t.chave === chave);
        setOcupado(true);
        try {
            const r = await ModeloTarefaService.criarConjunto({
                nome,
                chaveBiblioteca: pronto?.chave ?? null,
                layout: JSON.stringify(layoutPadrao()),
                etapas: pronto?.etapas ?? [],
            });
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível criar o trilho.'); return; }
            setCriando(false);
            aviso.ok('Trilho criado. Monte o quadro e ative quando quiser.');
            navegar(`/tarefas-padrao/${r.dados.conjuntoId}`);
        } finally {
            setOcupado(false);
        }
    };

    const duplicar = async (c: ConjuntoTrilho) => {
        setOcupado(true);
        try {
            const r = await ModeloTarefaService.duplicarConjunto(c.conjuntoId);
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível duplicar o trilho.'); return; }
            aviso.ok(r.mensagem || 'Trilho duplicado.');
            await carregar();
        } finally {
            setOcupado(false);
        }
    };

    const ativar = async (c: ConjuntoTrilho) => {
        setOcupado(true);
        try {
            const r = await ModeloTarefaService.ativarConjunto(c.conjuntoId);
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível ativar o trilho.'); return; }
            aviso.ok(r.mensagem || 'Trilho ativado.');
            await carregar();
        } finally {
            setOcupado(false); setAAtivar(null);
        }
    };

    const excluir = async (c: ConjuntoTrilho) => {
        setOcupado(true);
        try {
            const r = await ModeloTarefaService.excluirConjunto(c.conjuntoId);
            if (!r.sucesso) { aviso.erro(r.mensagem || 'Não foi possível excluir.'); return; }
            aviso.ok('Trilho excluído.');
            await carregar();
        } finally {
            setOcupado(false); setAExcluir(null);
        }
    };

    const ativo = conjuntos.find(c => c.ativo) ?? null;

    return (
        <div className="page-shell lk-screen tp-screen">
            <PageHeader
                title="Tarefas padrão"
                subtitle="O próximo passo que cada etapa cria. Um trilho fica ativo por vez."
                actions={
                    <div className="lk-actions">
                        {/* Histórico (fase 5): trilhos criados, quadros salvos, ativação, exclusão. */}
                        <HistoricoDaTela entidade="tarefas_padrao" titulo="Tarefas padrão" />
                        <button type="button" className="lk-btn is-primary" onClick={() => abrirNovo()}>
                            <LuPlus size={14} /> Novo trilho
                        </button>
                    </div>
                }
            />

            <div className="page-body tp-body">
                {parametros && !parametros.usaTarefasPadrao && (
                    <div className="tp-ban is-aviso">
                        <LuInfo size={16} />
                        <span>
                            As tarefas padrão estão <b>desligadas</b> para esta empresa: o trilho ativo fica pronto,
                            mas nenhuma tarefa nasce até ligar o parâmetro.
                        </span>
                        <Link className="lk-btn" to="/gerenciar-empresa">Abrir Parâmetros <LuArrowRight size={14} /></Link>
                    </div>
                )}
                {parametros && parametros.usaTarefasPadrao && !ativo && !carregando && (
                    <div className="tp-ban">
                        <LuInfo size={16} />
                        <span>O parâmetro está ligado, mas <b>nenhum trilho está ativo</b> — nada nasce até ativar um.</span>
                    </div>
                )}

                <div className="tp-sec">Da empresa</div>
                <div className="tp-gal">
                    {carregando
                        ? [1, 2, 3].map(i => <div key={i} className="tp-card is-skel" />)
                        : conjuntos.map(c => (
                            <article key={c.conjuntoId} className={`tp-card ${c.ativo ? 'is-ativo' : ''}`}>
                                <div className="tp-card-top">
                                    <span className="tp-card-ic"><LuRoute size={16} /></span>
                                    <h3 className="tp-card-nm">{c.nome}</h3>
                                    <span className={`tp-selo ${c.ativo ? '' : 'is-cinza'}`}>{c.ativo ? 'Ativo' : 'Inativo'}</span>
                                </div>
                                <p className="tp-card-d">
                                    {c.etapasComTarefa === 0
                                        ? 'Ainda sem etapa configurada — abra o quadro para montar.'
                                        : `${c.etapasComTarefa} ${c.etapasComTarefa === 1 ? 'etapa com tarefa' : 'etapas com tarefa'} · ${c.desfechos} ${c.desfechos === 1 ? 'desfecho' : 'desfechos'}`}
                                </p>
                                <div className="tp-card-m">
                                    <span>editado em {dataCurta(c.atualizadoEm)}</span>
                                </div>
                                <div className="tp-card-acts">
                                    <Link className="lk-btn" to={`/tarefas-padrao/${c.conjuntoId}`}>
                                        <LuPencil size={13} /> Abrir
                                    </Link>
                                    <button type="button" className="lk-btn" disabled={ocupado} onClick={() => duplicar(c)}>
                                        <LuCopy size={13} /> Duplicar
                                    </button>
                                    {!c.ativo && (
                                        <>
                                            <button type="button" className="lk-btn" disabled={ocupado} onClick={() => setAAtivar(c)}>
                                                <LuCheck size={13} /> Ativar
                                            </button>
                                            <button type="button" className="lk-btn is-danger" disabled={ocupado}
                                                aria-label={`Excluir ${c.nome}`} onClick={() => setAExcluir(c)}>
                                                <LuTrash2 size={13} />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </article>
                        ))}

                    {!carregando && (
                        <button type="button" className="tp-card is-novo" onClick={() => abrirNovo()}>
                            <LuPlus size={18} />
                            <span className="tp-novo-t">Novo trilho</span>
                            <span className="tp-novo-s">em branco ou a partir de um pronto</span>
                        </button>
                    )}
                </div>

                <div className="tp-sec">Modelos prontos</div>
                <div className="tp-gal">
                    {TRILHOS_PRONTOS.map(t => (
                        <article key={t.chave} className="tp-card">
                            <div className="tp-card-top">
                                <span className="tp-card-ic">{t.icone}</span>
                                <h3 className="tp-card-nm">{t.nome}</h3>
                            </div>
                            <p className="tp-card-d">{t.descricao}</p>
                            <div className="tp-card-m">
                                <span>{t.etapas.length} etapas · {t.etapas.reduce((s, e) => s + e.desfechos.length, 0)} desfechos</span>
                            </div>
                            <div className="tp-card-acts">
                                <Link className="lk-btn" to={`/tarefas-padrao/p-${t.chave}`}>
                                    <LuEye size={13} /> Ver
                                </Link>
                                <button type="button" className="lk-btn" disabled={ocupado}
                                    onClick={() => abrirNovo(t.chave)}>
                                    <LuCopy size={13} /> Usar
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            <NovoTrilhoModal
                aberta={criando}
                inicial={inicial}
                existentes={conjuntos}
                criando={ocupado}
                onFechar={() => setCriando(false)}
                onCriar={criar}
            />

            <Dialog
                open={!!aAtivar}
                onClose={() => setAAtivar(null)}
                title={`Ativar «${aAtivar?.nome ?? ''}»?`}
                confirmLabel="Ativar"
                cancelLabel="Cancelar"
                onConfirm={() => aAtivar && ativar(aAtivar)}
            >
                {ativo
                    ? `«${ativo.nome}» deixa de ser o trilho em uso. As tarefas já criadas continuam como estão; só as próximas seguem o novo.`
                    : 'As oportunidades abertas que estiverem numa etapa com tarefa ganham o próximo passo na hora.'}
            </Dialog>

            <Dialog
                open={!!aExcluir}
                onClose={() => setAExcluir(null)}
                title={`Excluir «${aExcluir?.nome ?? ''}»?`}
                confirmLabel="Excluir"
                cancelLabel="Cancelar"
                danger
                onConfirm={() => aExcluir && excluir(aExcluir)}
            >
                As tarefas que este trilho já criou continuam onde estão. O trilho sai da galeria.
            </Dialog>
        </div>
    );
};

export default TarefasPadraoPage;
